import { hasUnsavedChanges } from "./unsaved-changes";
import { markUpdateRequired } from "./update-state";
import { captureBaselineBuildId } from "./version-marker";

/**
 * Recover from a stale chunk after a redeploy. A tab left open across a
 * deploy can request a lazy route chunk (Results, ReportViewer, monitoring)
 * that the host no longer serves; the SPA fallback answers with index.html,
 * the browser rejects it as a module, and Vite fires `vite:preloadError`.
 *
 * The deploy carries the previous deploy's assets forward, so this only
 * fires for tabs two or more deploys behind. Then:
 * - unsaved input on screen: do not reload; show the "refresh required"
 *   banner and let the user save first;
 * - otherwise: reload to pick up the fresh index.html, capped per build so a
 *   genuinely broken deploy cannot loop. The cap is keyed by the build that
 *   failed, so a later deploy in the same browser session gets a fresh budget.
 */
const CHUNK_RELOAD_KEY = "vardic:chunk-reload";
export const MAX_CHUNK_RELOAD_ATTEMPTS = 2;

interface ChunkReloadRecord {
  build: string | null;
  attempts: number;
}

// A failed read or write must not block recovery, but a failed *write*
// means the retry budget cannot persist — in that case we must not reload,
// or a genuinely broken deploy (and the exact storage-disabled case the
// catch exists for) would loop forever instead of stopping after the cap.
function readAttempts(build: string | null): number {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return 0;
    const record = JSON.parse(raw) as Partial<ChunkReloadRecord>;
    return record.build === build && typeof record.attempts === "number"
      ? record.attempts
      : 0;
  } catch {
    return 0;
  }
}

// Returns true when the record persisted; false when sessionStorage is
// unusable (Safari private browsing, storage disabled by policy, quota).
function writeAttempts(record: ChunkReloadRecord): boolean {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

export type ChunkErrorOutcome = "reloaded" | "deferred" | "exhausted";

export function handleChunkLoadError(
  build: string | null,
  reload: () => void = () => globalThis.location.reload(),
): ChunkErrorOutcome {
  if (hasUnsavedChanges()) {
    markUpdateRequired();
    return "deferred";
  }
  const attempts = readAttempts(build);
  if (attempts >= MAX_CHUNK_RELOAD_ATTEMPTS) {
    markUpdateRequired();
    return "exhausted";
  }
  if (!writeAttempts({ build, attempts: attempts + 1 })) {
    // Without a persisted counter the retry budget cannot bound a broken
    // deploy, so a reload here could loop forever. Do not reload; surface
    // the "refresh required" banner and let the user reload deliberately.
    markUpdateRequired();
    return "exhausted";
  }
  reload();
  return "reloaded";
}

export function installChunkReloadHandler(): void {
  globalThis.addEventListener("vite:preloadError", () => {
    handleChunkLoadError(captureBaselineBuildId());
  });
}
